/**
 * Core domain types for the Planner feature.
 *
 * Mirrors the legacy `src/pages/Planner/types/plannerTypes.ts` DTOs. The shapes
 * intentionally match the backend payload so we can swap the legacy service
 * imports for `features/planner/api` without remapping every call-site.
 *
 * Wave 8 ownership:
 *  - Agent A (this file): core scheduling DTOs, status enum, scheduling /
 *    asset / unplanned-work payloads.
 *  - Agent B: crew-availability extensions (add a sibling file).
 *  - Agent C: insights / analytics aggregations (add a sibling file).
 */

import type { PlannerActivityStatusValue } from '../schemas/planner-status.schema';

/* -------------------------------------------------------------------------- */
/* Enums                                                                       */
/* -------------------------------------------------------------------------- */

export const PlannerActivityType = {
  Job: 1,
  Training: 2,
  Meeting: 3,
  Other: 4,
} as const;
export type PlannerActivityTypeValue =
  (typeof PlannerActivityType)[keyof typeof PlannerActivityType];

export const PlannerActionIntent = {
  None: 0,
  Erect: 1,
  Modify: 2,
  Dismantle: 3,
  Mixed: 4,
  Inspection: 5,
  Maintenance: 6,
} as const;
export type PlannerActionIntentValue =
  (typeof PlannerActionIntent)[keyof typeof PlannerActionIntent];

export const PlannerExecutionStatus = {
  Planned: 0,
  Completed: 1,
  Partial: 2,
  NotDone: 3,
} as const;
export type PlannerExecutionStatusValue =
  (typeof PlannerExecutionStatus)[keyof typeof PlannerExecutionStatus];

export const ScaffoldTaskType = {
  Erect: 1,
  Modify: 2,
  Dismantle: 3,
  Inspection: 4,
  Maintenance: 5,
  General: 6,
} as const;
export type ScaffoldTaskTypeValue =
  (typeof ScaffoldTaskType)[keyof typeof ScaffoldTaskType];

export const AssetClass = {
  Vehicle: 1,
  Tool: 2,
  Equipment: 3,
} as const;
export type AssetClassValue = (typeof AssetClass)[keyof typeof AssetClass];

/* -------------------------------------------------------------------------- */
/* Activity card / scheduling grid row                                        */
/* -------------------------------------------------------------------------- */

export interface ExecutionSummary {
  erectCount: number;
  modifyCount: number;
  dismantleCount: number;
  inspectionCount: number;
  maintenanceCount: number;
  generalCount?: number;
}

/**
 * A single scheduled PlannerActivity — the unit of the scheduling grid.
 *
 * `scheduledStart` is the source of truth for the day-column placement, while
 * supervisor / crew assignments determine the row when the grid is in "Crew
 * lane" mode. Both pivots are derived in `lib/grid-helpers.ts`.
 */
export interface PlannerActivity {
  id: string;
  title: string;
  scheduledStart: string;
  scheduledEnd?: string;
  planningOrder?: number;
  status: PlannerActivityStatusValue | number;
  actionIntent: PlannerActionIntentValue | number;
  actionIntentName?: string;
  chargeInitials?: string;
  chargeType?: string;
  siteTypeColorHex?: string;
  taskTypeColorHex?: string;
  crewCount: number;
  assetCount: number;
  taskCount?: number;
  referenceId?: string;
  referenceType: number;
  supervisorUserId?: string;
  supervisorName?: string;
  siteName?: string;
  clientName?: string;
  priorityCustomer?: boolean;
  siteTypeName?: string;
  siteLatitude?: number;
  siteLongitude?: number;
  siteAddress?: string;
  jobNumber?: string;
  activityNumber?: string;
  continuationStep?: number;
  chainLength?: number;
  chainStartDate?: string;
  chainEndDate?: string;
  chainActivities?: Array<{ activityNumber: string; scheduledStart: string }>;
  notes?: string;
  rowVersion?: string;
  executionSummary?: ExecutionSummary;
}

/**
 * Crew / asset row shape returned on the detail endpoint (`GET /Planner/{id}`).
 *
 * The list endpoints (`/Planner/cards`) only return summary counts —
 * `crewCount` / `assetCount`. The full detail payload also includes the
 * `crewAssignments` and `assetAssignments` arrays which back the per-row
 * "Remove" buttons in the ActivityDrawer.
 *
 * Fields use the legacy snake-shape (camelCase) preserved by `normalizeCard`
 * on the legacy service; field names mirror the v1 backend contract.
 */
export interface PlannerCrewAssignment {
  /** Crew member's user id — used to fire the remove endpoint. */
  userId: string;
  userName?: string;
  name?: string;
  isSupervisor?: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
  avatarUrl?: string;
}

export interface PlannerAssetAssignment {
  assetId: string;
  assetName?: string;
  name?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  icon?: string;
  assetClass?: AssetClassValue | number;
}

/**
 * Full activity detail returned by `GET /Planner/{id}`. Extends the lean
 * `PlannerActivity` card shape with the crew/asset assignment arrays used by
 * the drawer's "Crew" + "Assets" sections.
 */
export interface PlannerActivityDetail extends PlannerActivity {
  crewAssignments?: PlannerCrewAssignment[];
  assetAssignments?: PlannerAssetAssignment[];
  jobId?: string;
  siteId?: string;
  clientId?: string;
}

/**
 * Lightweight day-grid summary row (e.g. month aggregate). API may return this
 * shape from `GET /Planner/month`.
 */
export interface PlannerActivitySummary {
  id: string;
  title: string;
  scheduledStart: string;
  scheduledEnd?: string;
  status: PlannerActivityStatusValue | number;
  actionIntent: PlannerActionIntentValue | number;
  actionIntentName?: string;
  executionSummary?: ExecutionSummary;
  crewCount: number;
  activityNumber?: string;
  continuationStep?: number;
  chainLength?: number;
}

/* -------------------------------------------------------------------------- */
/* Resource pools (crew + assets)                                              */
/* -------------------------------------------------------------------------- */

export interface CrewMember {
  userId: string;
  name: string;
  role?: string;
  avatarUrl?: string;
}

export interface CrewPool {
  date: string;
  available: CrewMember[];
  assigned?: CrewMember[];
}

export interface AssetItem {
  assetId: string;
  name: string;
  icon?: string;
  assetClass?: AssetClassValue | number;
  assetImage?: string;
  /** Display name composed by the backend (e.g. "Truck #4 (REG-123)"). */
  displayName?: string;
}

export interface AssetPool {
  date: string;
  available: AssetItem[];
  assigned?: AssetItem[];
}

/* -------------------------------------------------------------------------- */
/* Scheduling payloads                                                         */
/* -------------------------------------------------------------------------- */

export interface PlannerActivityCreatePayload {
  activityType: PlannerActivityTypeValue | number;
  actionIntent: PlannerActionIntentValue | number;
  clientId?: string;
  siteId?: string;
  jobId?: string;
  supervisorUserId: string;
  scheduledStart: string;
  scheduledEnd?: string;
  notes?: string;
  crewUserIds?: string[];
  assetIds?: string[];
}

export interface PlannerActivityUpdatePayload {
  rowVersion?: string;
  supervisorUserId: string;
  actionIntent?: PlannerActionIntentValue | number;
  scheduledStart?: string;
  scheduledEnd?: string;
  notes?: string;
  crewUserIds?: string[];
  assetIds?: string[];
}

export interface PlannerActivityTimePayload {
  newStart: string;
  newEnd?: string;
  rowVersion?: string;
}

export interface PlannerOrderUpdate {
  activityId: string;
  planningOrder: number;
}

export interface PlannerReorderPayload {
  updates: PlannerOrderUpdate[];
}

export interface PlannerCrewAssignmentsPayload {
  crewIds: string[];
}

export interface PlannerAssetAssignmentsPayload {
  assetIds: string[];
}

export interface PlannerSingleCrewAssignmentPayload {
  crewMemberId: string;
  isSupervisor?: boolean;
  rowVersion?: string;
}

export interface PlannerSingleAssetAssignmentPayload {
  assetId: string;
  rowVersion?: string;
}

export interface ChangeActivityStatusPayload {
  newStatus: PlannerActivityStatusValue;
  rowVersion?: string;
  /**
   * Optional reason — currently used by the Execution Capture "Reject"
   * flow so the field crew sees why the supervisor sent the activity back.
   */
  reason?: string;
}

/* -------------------------------------------------------------------------- */
/* Unplanned work                                                              */
/* -------------------------------------------------------------------------- */

export interface UnplannedWorkItem {
  workOrderTaskId: string;
  jobId: string;
  jobNumber: string;
  /** Task / job name (legacy field name preserved). */
  jobName: string;
  workOrderId: string;
  workOrderTaskDescription?: string;
  clientName: string;
  siteName: string;
  scaffoldId?: string;
  scaffoldCode?: string;
  taskType?: ScaffoldTaskTypeValue | number;
  actionIntent: PlannerActionIntentValue | number;
  actionIntentName: string;
  requestedStart?: string;
  requestedEnd?: string;
}

/**
 * Payload for `POST /Planner/from-unplanned-task` — creates a new activity in
 * the grid by scheduling an unplanned work-order task.
 */
export interface CreateActivityFromUnplannedTaskPayload {
  workOrderTaskId: string;
  supervisorUserId: string;
  startTime: string;
  endTime: string;
  crewIds?: string[];
  assetIds?: string[];
  notes?: string;
}

/* -------------------------------------------------------------------------- */
/* Execution capture                                                           */
/* -------------------------------------------------------------------------- */

export interface PlannerExecutionLine {
  id: string;
  plannerActivityId: string;
  workOrderTaskId: string;
  workOrderTaskNumber?: string;
  workOrderTaskDescription?: string;
  scaffoldId?: string;
  scaffoldNumber?: string;
  scaffoldTaskType: ScaffoldTaskTypeValue | number;
  scaffoldTaskTypeName: string;
  executionStatus: PlannerExecutionStatusValue | number;
  executionStatusName: string;
  notes?: string;
  createdAt: string;
  executionOrder?: number;
  isContinuation?: boolean;
  continuationStep?: number;
  chainStartDate?: string;
  chainEndDate?: string;
  chainLength?: number;
  chainActivities?: Array<{ activityNumber: string; scheduledStart: string }>;
  continuesForward?: boolean;
  continuedFromPrevious?: boolean;
}

export interface ExecutionCaptureResult {
  executionLineId: string;
  scaffoldId?: string;
  result: 'Completed' | 'Partial' | 'NotDone';
  /** Required if `result === 'Partial' | 'NotDone'`. */
  continueOn?: string;
}

export interface ExecutionCapturePayload {
  activityId: string;
  results: ExecutionCaptureResult[];
}

/* -------------------------------------------------------------------------- */
/* Grid filters / query inputs                                                 */
/* -------------------------------------------------------------------------- */

export type SchedulingGridMode = 'crew' | 'asset';

export type PlannerViewSpan = 'day' | 'week' | '2weeks' | 'month';

export interface SchedulingGridFilters {
  /** Inclusive ISO date (YYYY-MM-DD) — start of the grid range. */
  from: string;
  /** Inclusive ISO date (YYYY-MM-DD) — end of the grid range. */
  to: string;
  /** Row-axis pivot (defaults to `'crew'`). */
  mode?: SchedulingGridMode;
  /** Optional supervisor scope (Agent B may extend). */
  supervisorUserId?: string;
}

export interface PlannerActivityListFilters {
  from: string;
  to: string;
}

export interface UnplannedWorkFilters {
  /** Free-text search applied client-side. */
  search?: string;
}

export interface AssetPoolFilters {
  /** ISO date the pool is being computed for. */
  date: string;
  /** Optional asset class filter (1=Vehicle, 2=Tool, 3=Equipment). */
  assetClass?: AssetClassValue | number;
}

export interface CrewPoolFilters {
  date: string;
}

/* -------------------------------------------------------------------------- */
/* Re-exports for the public types barrel                                      */
/* -------------------------------------------------------------------------- */

export type { PlannerActivityStatusValue } from '../schemas/planner-status.schema';
